/**
 * AI Intent Detector
 * 
 * Detects user intent for AI creation tasks (data models, image generation, image enhancement)
 * Uses hybrid approach: keyword-based pattern matching + TensorFlow Universal Sentence Encoder
 * for semantic similarity scoring.
 */

// Import types only, not implementations to avoid loading TensorFlow at startup
import type * as use from '@tensorflow-models/universal-sentence-encoder';
import type * as tf from '@tensorflow/tfjs';

export type AIIntent = 'dataModel' | 'imageGenerate' | 'imageEnhance' | 'general';

export interface IntentResult {
  intent: AIIntent;
  confidence: number;
  metadata?: {
    enhancementType?: 'upscale' | 'stylize' | 'remove-background';
  };
}

export type WindowComponentType = 'hierarchy' | 'canvas' | 'editor' | 'dataHandler' | 'document' | null;

// Lazy load TensorFlow modules
let tfModule: typeof tf | null = null;
let useModule: typeof use | null = null;
let sentenceEncoderModel: any | null = null;
let modelLoadPromise: Promise<void> | null = null;

async function loadTensorFlowModules() {
  if (!tfModule || !useModule) {
    [tfModule, useModule] = await Promise.all([
      import('@tensorflow/tfjs'),
      import('@tensorflow-models/universal-sentence-encoder')
    ]);
  }
  return { tf: tfModule, use: useModule };
}

async function loadSentenceEncoder(): Promise<any> {
  if (sentenceEncoderModel) {
    return sentenceEncoderModel;
  }

  if (modelLoadPromise) {
    return modelLoadPromise.then(() => sentenceEncoderModel);
  }

  modelLoadPromise = (async () => {
    try {
      const { use } = await loadTensorFlowModules();
      // Try QnA model first (better for question-answer similarity), fallback to regular USE
      try {
        sentenceEncoderModel = await use.loadQnA();
      } catch {
        sentenceEncoderModel = await use.load();
      }
    } catch (error) {
      console.warn('⚠️ [AIIntentDetector] Failed to load sentence encoder, using keyword-only mode:', error);
      sentenceEncoderModel = null;
    } finally {
      modelLoadPromise = null;
    }
  })();

  return modelLoadPromise.then(() => sentenceEncoderModel);
}

/**
 * Keywords for each intent type (for keyword-based scoring)
 * Higher weight keywords indicate stronger intent
 */
const INTENT_KEYWORDS = {
  dataModel: {
    high: ['data model', 'database', 'schema', 'data structure', 'entity model', 'table structure'],
    medium: ['model', 'fields', 'columns', 'entity', 'table', 'crm', 'inventory', 'management system'],
    low: ['data', 'structure', 'form', 'record']
  },
  imageGenerate: {
    high: ['generate image', 'create image', 'make picture', 'draw', 'visualize'],
    medium: ['image', 'picture', 'photo', 'illustration', 'graphic'],
    low: ['show', 'display', 'render']
  },
  imageEnhance: {
    high: ['enhance image', 'upscale image', 'stylize image', 'remove background', 'improve image'],
    medium: ['enhance', 'upscale', 'stylize', 'edit image', 'better image'],
    low: ['improve', 'edit', 'modify']
  }
};

/**
 * Reference sentences for semantic similarity matching
 * These represent canonical examples of each intent
 */
const INTENT_REFERENCE_SENTENCES = {
  dataModel: [
    'create a data model for',
    'build a database schema',
    'design a data structure',
    'generate a schema for',
    'define a data model',
    'create database model',
    'build data schema'
  ],
  imageGenerate: [
    'generate an image of',
    'create a picture of',
    'make an image showing',
    'draw a picture of',
    'visualize',
    'create image',
    'generate picture'
  ],
  imageEnhance: [
    'enhance the image',
    'upscale this image',
    'stylize the image',
    'remove background from image',
    'improve image quality',
    'make image better',
    'edit the image'
  ]
};

/**
 * Keyword patterns for intent detection
 * Higher priority patterns are checked first and have higher weight
 */
const INTENT_PATTERNS = {
  dataModel: {
    high: [
      /create\s+(a\s+)?data\s+model/i,
      /build\s+(a\s+)?data\s+model/i,
      /build\s+(a\s+)?(data\s+)?schema/i,
      /generate\s+(a\s+)?(data\s+)?schema/i,
      /data\s+model\s+for/i,
      /schema\s+for/i,
      /database\s+model/i,
      /data\s+structure\s+for/i,
      /create\s+(a\s+)?database/i,
      /build\s+(a\s+)?database/i,
      /design\s+(a\s+)?(data\s+)?schema/i,
      /design\s+(a\s+)?database/i,
      /new\s+(data\s+)?model/i,
      /new\s+(data\s+)?schema/i,
      /define\s+(a\s+)?(data\s+)?schema/i,
      /define\s+(a\s+)?database/i,
    ],
    medium: [
      /database/i,
      /data\s+structure/i,
      /fields?\s+for/i,
      /create\s+model/i,
      /build\s+model/i,
      /data\s+model/i,
      /schema/i,
      /data\s+table/i,
      /table\s+structure/i,
      /entity\s+model/i,
      /entity\s+schema/i,
      /crm\s+database/i,
      /inventory\s+system/i,
      /management\s+system/i,
    ],
  },
  imageGenerate: {
    high: [
      /generate\s+(an?\s+)?image/i,
      /create\s+(an?\s+)?image/i,
      /make\s+(a\s+)?picture/i,
      /draw\s+(me\s+)?(an?\s+)?/i,
      /image\s+of/i,
      /picture\s+of/i,
      /visualize/i,
      /show\s+me\s+(an?\s+)?image/i,
    ],
    medium: [
      /image/i,
      /picture/i,
      /photo/i,
      /illustration/i,
      /graphic/i,
    ],
  },
  imageEnhance: {
    high: [
      /enhance\s+(the\s+)?image/i,
      /upscale\s+(the\s+)?image/i,
      /stylize\s+(the\s+)?image/i,
      /remove\s+background/i,
      /improve\s+(the\s+)?image/i,
      /make\s+(the\s+)?image\s+better/i,
      /edit\s+(the\s+)?image/i,
    ],
    medium: [
      /enhance/i,
      /upscale/i,
      /stylize/i,
      /improve\s+image/i,
      /better\s+image/i,
    ],
  },
};

/**
 * Enhancement type detection patterns
 */
const ENHANCEMENT_TYPE_PATTERNS = {
  upscale: /upscale|increase\s+resolution|higher\s+quality|make\s+bigger/i,
  stylize: /stylize|style|artistic|apply\s+style/i,
  'remove-background': /remove\s+background|transparent\s+background|no\s+background/i,
};

/**
 * Extract keywords from query and calculate keyword-based score
 */
function calculateKeywordScore(
  normalizedQuery: string,
  intentType: string
): number {
  const keywords = INTENT_KEYWORDS[intentType as keyof typeof INTENT_KEYWORDS];
  if (!keywords) return 0;

  let score = 0;
  const queryWords = normalizedQuery.split(/\s+/);

  // Check high priority keywords (weight: 0.8 per match, max 2 matches)
  let highMatches = 0;
  for (const keyword of keywords.high) {
    if (normalizedQuery.includes(keyword.toLowerCase())) {
      score += 0.8;
      highMatches++;
      if (highMatches >= 2) break; // Cap at 2 high matches
    }
  }

  // Check medium priority keywords (weight: 0.4 per match, max 3 matches)
  if (highMatches === 0) {
    let mediumMatches = 0;
    for (const keyword of keywords.medium) {
      if (normalizedQuery.includes(keyword.toLowerCase())) {
        score += 0.4;
        mediumMatches++;
        if (mediumMatches >= 3) break; // Cap at 3 medium matches
      }
    }
  }

  // Check low priority keywords (weight: 0.1 per match, max 2 matches, only if no high/medium)
  if (highMatches === 0 && score === 0) {
    let lowMatches = 0;
    for (const keyword of keywords.low) {
      if (queryWords.includes(keyword.toLowerCase())) {
        score += 0.1;
        lowMatches++;
        if (lowMatches >= 2) break; // Cap at 2 low matches
      }
    }
  }

  return Math.min(score, 2.0); // Cap total keyword score at 2.0
}

/**
 * Calculate semantic similarity using TensorFlow Universal Sentence Encoder
 */
async function calculateSemanticSimilarity(
  query: string,
  intentType: string
): Promise<number> {
  try {
    const model = await loadSentenceEncoder();
    if (!model || !model.embed) {
      return 0; // Fallback to keyword-only if model unavailable
    }

    const { tf } = await loadTensorFlowModules();
    if (!tf) return 0;

    const referenceSentences = INTENT_REFERENCE_SENTENCES[intentType as keyof typeof INTENT_REFERENCE_SENTENCES];
    if (!referenceSentences || referenceSentences.length === 0) {
      return 0;
    }

    // Embed query and all reference sentences
    const textsToEmbed = [query.toLowerCase(), ...referenceSentences];
    const embeddings = await model.embed(textsToEmbed);
    
    if (!embeddings) return 0;

    const maxSimilarity = tf.tidy(() => {
      const queryEmbedding = tf.slice(embeddings, [0, 0], [1, -1]);
      let maxSim = 0;

      // Calculate cosine similarity with each reference sentence
      for (let i = 0; i < referenceSentences.length; i++) {
        const refEmbedding = tf.slice(embeddings, [i + 1, 0], [1, -1]);
        
        // Cosine similarity: dot product / (norm1 * norm2)
        const dotProduct = tf.matMul(queryEmbedding, refEmbedding, false, true);
        const normQuery = tf.norm(queryEmbedding, 'euclidean', 1, true);
        const normRef = tf.norm(refEmbedding, 'euclidean', 1, true);
        const similarity = tf.div(dotProduct, tf.mul(normQuery, normRef));
        
        const similarityValue = similarity.dataSync();
        const sim = Array.isArray(similarityValue) ? similarityValue[0] : similarityValue;
        
        if (sim > maxSim) {
          maxSim = sim;
        }
      }

      return maxSim;
    });

    // Cleanup embeddings
    embeddings.dispose();

    // Normalize similarity (0-1 range, but boost it slightly for better routing)
    // Semantic similarity typically ranges 0.3-0.9, so we scale it to 0-1.5 range
    return Math.min(1.5, maxSimilarity * 1.5);
  } catch (error) {
    console.warn('⚠️ [AIIntentDetector] Semantic similarity calculation failed, using keyword-only:', error);
    return 0; // Fallback to keyword-only
  }
}

/**
 * Detects AI creation intent from user query
 * Uses hybrid approach: regex patterns + keyword matching + semantic similarity
 * 
 * @param query - User's query text
 * @param activeWindow - Currently active window component (for context boost)
 * @param hasSelectedImageLayer - Whether user has selected an image layer in canvas (for enhancement detection)
 * @returns Promise<IntentResult> with detected intent, confidence score, and metadata
 */
export async function detectAICreationIntent(
  query: string,
  activeWindow?: WindowComponentType,
  hasSelectedImageLayer?: boolean
): Promise<IntentResult> {
  if (!query || typeof query !== 'string') {
    return { intent: 'general', confidence: 0 };
  }

  // Normalize query: remove extra spaces, handle common variations
  const normalizedQuery = query
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' '); // Normalize multiple spaces

  const results: Array<{ intent: AIIntent; score: number; metadata?: any }> = [];

  // Check each intent type
  for (const [intentType, patterns] of Object.entries(INTENT_PATTERNS)) {
    let regexScore = 0;
    let hasHighMatch = false;

    // 1. REGEX PATTERN MATCHING (existing approach)
    // Check high priority patterns (weight: 1.0)
    for (const pattern of patterns.high) {
      if (pattern.test(normalizedQuery)) {
        regexScore += 1.0;
        hasHighMatch = true;
        console.log(`🎯 [AIIntentDetector] ${intentType} - High priority regex match:`, pattern.toString());
        break; // Only count first high-priority match to avoid double-counting
      }
    }

    // Check medium priority patterns (weight: 0.5) only if no high-priority match
    if (!hasHighMatch) {
      for (const pattern of patterns.medium) {
        if (pattern.test(normalizedQuery)) {
          regexScore += 0.5;
          console.log(`🎯 [AIIntentDetector] ${intentType} - Medium priority regex match:`, pattern.toString());
          break; // Only count first medium-priority match
        }
      }
    }

    // 2. KEYWORD-BASED SCORING (new approach)
    const keywordScore = calculateKeywordScore(normalizedQuery, intentType);
    if (keywordScore > 0) {
      console.log(`🔑 [AIIntentDetector] ${intentType} - Keyword score:`, keywordScore);
    }

    // 3. SEMANTIC SIMILARITY SCORING (new approach using TensorFlow)
    // Skip semantic similarity if we already have a high regex match (faster routing)
    let semanticScore = 0;
    if (!hasHighMatch) {
      // Only calculate semantic similarity if no high regex match (to speed up common cases)
      try {
        console.log(`🧠 [AIIntentDetector] ${intentType} - Starting semantic similarity calculation...`);
        semanticScore = await Promise.race([
          calculateSemanticSimilarity(query, intentType),
          new Promise<number>((resolve) => setTimeout(() => {
            console.warn(`⏱️ [AIIntentDetector] ${intentType} - Semantic similarity timeout, using 0`);
            resolve(0);
          }, 1500)) // 1.5 second timeout (faster for better UX)
        ]);
        if (semanticScore > 0) {
          console.log(`🧠 [AIIntentDetector] ${intentType} - Semantic similarity score:`, semanticScore);
        } else {
          console.log(`🧠 [AIIntentDetector] ${intentType} - Semantic similarity score: 0 (timeout or unavailable)`);
        }
      } catch (error) {
        console.warn(`⚠️ [AIIntentDetector] ${intentType} - Semantic similarity error, using 0:`, error);
        semanticScore = 0;
      }
    } else {
      console.log(`⏭️ [AIIntentDetector] ${intentType} - Skipping semantic similarity (high regex match found)`);
    }

    // Combine all three scores with weights:
    // - Regex patterns: 40% (most reliable for exact matches)
    // - Keywords: 30% (good for partial matches)
    // - Semantic similarity: 30% (catches semantic variations)
    let combinedScore = (regexScore * 0.4) + (keywordScore * 0.3) + (semanticScore * 0.3);
    
    console.log(`📊 [AIIntentDetector] ${intentType} - Score breakdown:`, {
      regexScore,
      keywordScore,
      semanticScore,
      combinedScore: combinedScore.toFixed(3),
      weights: '40% regex, 30% keyword, 30% semantic'
    });

    // Context boost based on active window (applied after combining scores)
    const contextBoost = 
      (intentType === 'dataModel' && activeWindow === 'dataHandler') ? 0.8 :
      (intentType === 'imageGenerate' && activeWindow === 'canvas') ? 0.6 :
      (intentType === 'imageEnhance' && activeWindow === 'canvas' && hasSelectedImageLayer) ? 0.9 :
      (intentType === 'imageEnhance' && activeWindow === 'canvas') ? 0.4 : 0;
    
    if (contextBoost > 0) {
      combinedScore += contextBoost;
      console.log(`🎁 [AIIntentDetector] ${intentType} - Context boost applied:`, contextBoost);
    }
    
    console.log(`📊 [AIIntentDetector] ${intentType} - Final combined score:`, combinedScore.toFixed(3));

    // Only add to results if we have some signal from at least one method
    if (combinedScore > 0 || regexScore > 0 || keywordScore > 0 || semanticScore > 0) {
      // Detect enhancement type for imageEnhance intent
      let metadata;
      if (intentType === 'imageEnhance') {
        for (const [enhancementType, pattern] of Object.entries(ENHANCEMENT_TYPE_PATTERNS)) {
          if (pattern.test(normalizedQuery)) {
            metadata = { enhancementType: enhancementType as 'upscale' | 'stylize' | 'remove-background' };
            combinedScore += 0.3; // Bonus for specific enhancement type
            break;
          }
        }
      }

      results.push({
        intent: intentType as AIIntent,
        score: combinedScore,
        metadata,
      });
    }
  }

  // If no intent detected, return general
  if (results.length === 0) {
    return { intent: 'general', confidence: 0 };
  }

  // Sort by score (highest first)
  results.sort((a, b) => b.score - a.score);

  const topResult = results[0];
  
  // Normalize confidence to 0-1 range
  // Combined scores can range from 0 to ~4.0 (regex: 1.0, keyword: 2.0, semantic: 1.5, context: 0.9)
  // However, with just regex + keyword (no semantic), typical score is ~0.6-1.0
  // We need to ensure high regex matches get at least 0.5 confidence
  // Strategy: Use a lower normalization base, but boost for high regex matches
  
  // Calculate what the regex contribution likely was
  // If score is >= 0.4, it likely has a high regex match (1.0 * 0.4 = 0.4)
  const likelyHasHighRegexMatch = topResult.score >= 0.4;
  
  // Use adaptive normalization based on score range
  // For scores < 1.0 (regex + keyword only), use 1.0 as max
  // For scores >= 1.0 (includes semantic/context), use 2.0 as max
  const maxExpectedScore = topResult.score < 1.0 ? 1.0 : 2.0;
  let confidence = Math.min(1.0, topResult.score / maxExpectedScore);
  
  // Boost confidence if we have a high regex match (most reliable signal)
  if (likelyHasHighRegexMatch && topResult.intent !== 'general') {
    // Ensure minimum confidence of 0.5 for high regex matches
    confidence = Math.max(confidence, 0.5);
    console.log('🚀 [AIIntentDetector] High regex match detected - boosting confidence to minimum 0.5');
  }

  console.log('🏆 [AIIntentDetector] Final result:', {
    intent: topResult.intent,
    rawScore: topResult.score.toFixed(3),
    confidence: confidence.toFixed(3),
    maxExpectedScore,
    allResults: results.map(r => ({ intent: r.intent, score: r.score.toFixed(3) }))
  });

  return {
    intent: topResult.intent,
    confidence,
    metadata: topResult.metadata,
  };
}

/**
 * Synchronous version that uses only regex and keyword matching (no TensorFlow)
 * Useful for quick checks or when TensorFlow is unavailable
 * 
 * @param query - User's query text
 * @param activeWindow - Currently active window component (for context boost)
 * @param hasSelectedImageLayer - Whether user has selected an image layer in canvas
 * @returns IntentResult with detected intent, confidence score, and metadata
 */
export function detectAICreationIntentSync(
  query: string,
  activeWindow?: WindowComponentType,
  hasSelectedImageLayer?: boolean
): IntentResult {
  if (!query || typeof query !== 'string') {
    return { intent: 'general', confidence: 0 };
  }

  const normalizedQuery = query
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

  const results: Array<{ intent: AIIntent; score: number; metadata?: any }> = [];

  for (const [intentType, patterns] of Object.entries(INTENT_PATTERNS)) {
    let regexScore = 0;
    let hasHighMatch = false;

    // Regex pattern matching
    for (const pattern of patterns.high) {
      if (pattern.test(normalizedQuery)) {
        regexScore += 1.0;
        hasHighMatch = true;
        break;
      }
    }

    if (!hasHighMatch) {
      for (const pattern of patterns.medium) {
        if (pattern.test(normalizedQuery)) {
          regexScore += 0.5;
          break;
        }
      }
    }

    // Keyword-based scoring
    const keywordScore = calculateKeywordScore(normalizedQuery, intentType);

    // Combine regex and keyword scores (no semantic similarity in sync version)
    let combinedScore = (regexScore * 0.6) + (keywordScore * 0.4);

    // Context boost
    if (intentType === 'dataModel' && activeWindow === 'dataHandler') {
      combinedScore += 0.8;
    } else if (intentType === 'imageGenerate' && activeWindow === 'canvas') {
      combinedScore += 0.6;
    } else if (intentType === 'imageEnhance' && activeWindow === 'canvas' && hasSelectedImageLayer) {
      combinedScore += 0.9;
    } else if (intentType === 'imageEnhance' && activeWindow === 'canvas') {
      combinedScore += 0.4;
    }

    if (combinedScore > 0 || regexScore > 0 || keywordScore > 0) {
      let metadata;
      if (intentType === 'imageEnhance') {
        for (const [enhancementType, pattern] of Object.entries(ENHANCEMENT_TYPE_PATTERNS)) {
          if (pattern.test(normalizedQuery)) {
            metadata = { enhancementType: enhancementType as 'upscale' | 'stylize' | 'remove-background' };
            combinedScore += 0.3;
            break;
          }
        }
      }

      results.push({
        intent: intentType as AIIntent,
        score: combinedScore,
        metadata,
      });
    }
  }

  if (results.length === 0) {
    return { intent: 'general', confidence: 0 };
  }

  results.sort((a, b) => b.score - a.score);
  const topResult = results[0];
  
  // Normalize for sync version (max score ~2.5 without semantic)
  const maxExpectedScore = 2.5;
  const confidence = Math.min(1.0, topResult.score / maxExpectedScore);

  return {
    intent: topResult.intent,
    confidence,
    metadata: topResult.metadata,
  };
}

/**
 * Checks if query has high confidence for a specific intent
 * 
 * @param result - IntentResult from detectAICreationIntent
 * @param threshold - Confidence threshold (default: 0.7)
 * @returns true if confidence meets threshold
 */
export function hasHighConfidence(result: IntentResult, threshold: number = 0.7): boolean {
  return result.confidence >= threshold && result.intent !== 'general';
}

